import React, { useEffect, useRef, useState } from 'react'

import styled from '@emotion/styled'
import {
  AlignItems,
  Body2,
  Body3,
  BorderStyle,
  Box,
  Color,
  Space,
  Heading4,
  Heading6,
  Icon,
  Modal,
  ModalBody,
  ModalButton,
  ModalClose,
  ModalFooter,
  ModalHeader,
  RadioGroup,
  Strong,
  useModal,
} from '@gousto-internal/citrus-react'
import type Immutable from 'immutable'
import { useDispatch, useSelector, useStore } from 'react-redux'

import type { ShippingAddress } from '@library/api-user'
import { queueTrackingEvent } from '@library/tracking-integration'

import { actionTypes } from 'actions/actionTypes'
import {
  basketChosenAddressChange,
  basketAddressChange,
  basketPostcodeChange,
} from 'actions/basket'
import { basketDeliveryDaysReceive } from 'actions/boxSummary'
import tempActions from 'actions/temp'
import type { BasketAddress } from 'reducers/basket'
import { DELIVERY_ADDRESS_MODAL_NAME } from 'routes/DeliveryDetails/constants'
import { getDisabledSlotDates } from 'routes/Menu/selectors/boxSummary'
import { getIsAuthenticated } from 'selectors/auth'
import { getChosenAddress } from 'selectors/basket'
import { getUserOrders, getUserShippingAddresses } from 'selectors/user'
import { getDeliveryDaysAndSlots, getTempDeliveryOptions } from 'utils/deliverySlotHelper'

const StyledRadioContainer = styled.div`
  label {
    align-items: flex-start;
  }
`

const InfoBox = () => (
  <Box
    display="flex"
    alignItems={AlignItems.FlexStart}
    bg={Color.Informative_50}
    paddingH={3}
    paddingV={4}
    borderWidth={1}
    borderColor={Color.Informative_200}
    borderRadius={1}
    borderStyle={BorderStyle.Solid}
  >
    <Icon name="info" />
    <Box paddingH={2}>
      <Heading6>Want to add a new address?</Heading6>
      <Body3>
        Head to the <Strong color={Color.Secondary_400}>Delivery info</Strong> section of your
        account details page.
      </Body3>
    </Box>
  </Box>
)

const DeliveryAddressModal = () => {
  const { closeCurrentModal, getCurrentModal } = useModal()
  const dispatch = useDispatch()
  const store = useStore()
  const deliveryAddresses: Immutable.List<Immutable.Map<keyof ShippingAddress, unknown>> =
    useSelector(getUserShippingAddresses)
  const chosenAddress = useSelector(getChosenAddress) as BasketAddress
  const [addressId, setAddressId] = useState(chosenAddress.get('id'))
  const [isSubmitting, setIsSubmitting] = useState(false)

  // changeAddress is an async closure - by the time it resumes after its await, the customer may
  // have opened a different modal, making the address modal no longer "current". A ref (mutated
  // on every render, unlike a value captured in changeAddress' own closure) is read for the
  // freshest value regardless of which render's closure is still running.
  const currentModalRef = useRef<string | null | undefined>(null)
  const currentModal = getCurrentModal()
  currentModalRef.current = currentModal

  // Matching the modal name isn't enough on its own: dismissing this modal while a submission is
  // pending and then reopening it produces the *same* name, but is a fresh session the customer
  // is actively interacting with. Bump a counter each time the modal transitions into being
  // current, and have each submission remember its own session so a stale one can recognise it's
  // no longer the session it started in, even though the name still matches.
  const modalSessionIdRef = useRef(0)
  const wasCurrentModalRef = useRef<string | null | undefined>(null)
  useEffect(() => {
    if (
      currentModal === DELIVERY_ADDRESS_MODAL_NAME &&
      wasCurrentModalRef.current !== DELIVERY_ADDRESS_MODAL_NAME
    ) {
      modalSessionIdRef.current += 1
    }
    wasCurrentModalRef.current = currentModal
  })

  const getRadioLabel = (address: ShippingAddress) => {
    const { id, name, line1, line2, line3, town, postcode } = address

    return (
      <Box paddingH={2}>
        <Body2 fontWeight={700} color={id === addressId ? Color.Secondary_400 : Color.ColdGrey_800}>
          {name}
        </Body2>
        <Body3>{line1}</Body3>
        <Body3>{line2}</Body3>
        <Body3>{line3}</Body3>
        <Body3>
          {town}, {postcode}
        </Body3>
      </Box>
    )
  }

  const getRadioOptions = () =>
    deliveryAddresses.toJS().map((address: ShippingAddress) => ({
      value: address?.id,
      label: getRadioLabel(address),
    }))

  // The reload this modal triggers is awaited, so by the time it settles the customer may have
  // already dismissed this modal (e.g. via its close control) and opened a different one (the box
  // size editor, say) - or dismissed and reopened this same modal, starting a fresh session.
  // closeCurrentModal() would blindly close whichever modal is "current" at that point - only
  // close if this modal is still current AND it's still the same session this submission
  // started in, rather than dismissing an interaction the customer has since started.
  const closeThisModalIfStillCurrent = (mySessionId: number) => {
    if (
      currentModalRef.current === DELIVERY_ADDRESS_MODAL_NAME &&
      modalSessionIdRef.current === mySessionId
    ) {
      closeCurrentModal()
    }
  }

  // Used both when the reload request itself rejects (e.g. the active-periods cutoff lookup
  // fails) and when it resolves but boxSummaryDeliveryDaysLoad's catch block records an error
  // (e.g. a "do-not-deliver" postcode). Either way boxSummaryDeliveryDays is left holding the
  // *old* address's (genuinely deliverable) data, so it must be invalidated explicitly -
  // otherwise the next landing-day fallback would treat that stale data as valid for the new
  // address and silently re-enable "Choose recipes" with an old-postcode slot.
  const blockFlowAfterReloadFailure = (mySessionId: number) => {
    dispatch(basketDeliveryDaysReceive({}))
    // basketPostcodeChange can reject before ever reaching boxSummaryDeliveryDaysLoad (e.g. its
    // internal cutoff lookup failing), so no request-id bump happens for this attempt. Any other
    // in-flight request (e.g. DeliveryDetails' mount-time load, still fetching the old postcode)
    // would otherwise still consider itself current and could repopulate boxSummaryDeliveryDays
    // with old-postcode data once it resolves, after this handler has just cleared it. Bumping
    // the counter here supersedes any such request too, not just ones this modal itself started.
    dispatch({ type: actionTypes.BOXSUMMARY_DELIVERY_DAYS_REQUEST_START })
    dispatch(tempActions.tempReset())
    dispatch(tempActions.temp('addressChangeWarning', 'unavailable'))
    // basketPostcodeChange may have already dispatched BASKET_POSTCODE_PENDING: true - if its own
    // matching false dispatch was skipped (e.g. the reload rejected before reaching it), this
    // would otherwise stay stuck true and permanently block postcode-pending consumers elsewhere
    // (e.g. useGetCurrentBoxSummaryView). Harmless to dispatch again if it was already cleared.
    dispatch({ type: actionTypes.BASKET_POSTCODE_PENDING, pending: false })
    closeThisModalIfStillCurrent(mySessionId)

    queueTrackingEvent<{
      action: 'confirm_delivery_address'
    }>({
      action: 'confirm_delivery_address',
    })
  }

  const changeAddress = async () => {
    // Guard against a second confirmation being submitted while the first address's reload is
    // still in flight - without this, two overlapping postcode reloads can race against the same
    // unversioned boxSummaryDeliveryDays state, and whichever resolves last "wins" even if it
    // belongs to the address the customer no longer has selected.
    if (isSubmitting) {
      return
    }

    setIsSubmitting(true)
    // Captured once at the start of this submission so it can recognise, once its await
    // resolves, whether this modal has since been dismissed and reopened as a new session -
    // currentModalRef alone can't distinguish that from "still the same open modal".
    const mySessionId = modalSessionIdRef.current

    try {
      const selectedAddress = deliveryAddresses
        .filter((address) => address?.get('id') === addressId)
        .first()
      const newPostcode = selectedAddress.get('postcode')

      // Capture what's currently shown on the Delivery details page before the address (and
      // therefore the available delivery days) changes, so it can be revalidated afterwards.
      const stateBeforeChange = store.getState()
      const { tempDate: previousTempDate, tempSlotId: previousTempSlotId } =
        getTempDeliveryOptions(stateBeforeChange)

      dispatch(basketAddressChange(selectedAddress))
      dispatch(basketChosenAddressChange(selectedAddress))

      let reloadResult: { success: boolean; error?: string; discarded?: boolean } | undefined
      try {
        // basketPostcodeChange (unlike basketPostcodeChangePure) also reloads the delivery
        // days/slots for the new postcode - basketPostcodeChangePure only ever updates
        // basket.postcode. It can itself reject (e.g. its internal active-periods cutoff lookup
        // failing) without ever reaching boxSummaryDeliveryDaysLoad's own error handling below.
        // Force 'transactional' explicitly - basket.orderId can still refer to a subscription
        // order (e.g. after navigating back from /menu/:orderId), and Delivery details is always
        // a transactional flow regardless of what the basket currently points at.
        reloadResult = (await dispatch(
          basketPostcodeChange(newPostcode, false, 'transactional'),
        )) as unknown as { success: boolean; error?: string; discarded?: boolean } | undefined
      } catch {
        blockFlowAfterReloadFailure(mySessionId)

        return
      }

      // A newer request (for this postcode or another) was already validated and applied before
      // this one settled - boxSummaryDeliveryDays and temp now belong to that winning request, not
      // this one. This request's own outcome (whether it would have been a success or a failure)
      // no longer reflects reality and must not be acted on: neither treating it as a real error
      // (which would wipe the newer, valid data blockFlowAfterReloadFailure doesn't own) nor as a
      // success (there's nothing of this request's own to apply). Simply stop.
      if (reloadResult?.discarded) {
        return
      }

      // boxSummaryDeliveryDaysLoad's catch block (e.g. the "do-not-deliver" error for a postcode
      // with no delivery days at all) never calls basketDeliveryDaysReceive, so on failure
      // boxSummaryDeliveryDays is left completely unchanged - still the *old* address's data, not
      // an empty result. Validating previousTempDate/previousTempSlotId against that would
      // silently "pass" (it's literally the same data), incorrectly clearing the warning even
      // though we have no idea whether the new address can be delivered to at all. Treat a failed
      // reload the same way as "no valid days". Read this from the reload's own propagated
      // outcome rather than shared/global error state - a concurrent request for the same
      // postcode (e.g. DeliveryDetails' mount-time load) can write to that same shared key, so it
      // can't reliably distinguish "this request failed" from "a different one did".
      if (reloadResult?.success === false) {
        blockFlowAfterReloadFailure(mySessionId)

        return
      }

      const stateAfterChange = store.getState()

      const { deliveryDays: reloadedDeliveryDays } = getTempDeliveryOptions(stateAfterChange)
      const { deliveryDays: sortedDeliveryDays, slots } = getDeliveryDaysAndSlots(
        previousTempDate,
        {
          disabledSlots: getDisabledSlotDates(stateAfterChange),
          isAuthenticated: getIsAuthenticated(stateAfterChange),
          tempDate: previousTempDate,
          userOrders: getUserOrders(stateAfterChange),
          tempSlotId: previousTempSlotId,
          deliveryDaysProps: reloadedDeliveryDays,
        },
      )

      // The previous day is only genuinely still valid if it's present AND not disabled (e.g. via
      // alternateDeliveryDay) - being present in the list is not enough, since disabled days are
      // still listed. Likewise the previously-selected slot must specifically still exist and be
      // active on that day - some other slot being active on the same date isn't sufficient,
      // since that isn't the slot the customer actually chose.
      const previousDayEntry = sortedDeliveryDays.find(
        (deliveryDay) => deliveryDay.date === previousTempDate,
      )
      const isPreviousDayStillValid = Boolean(previousDayEntry && !previousDayEntry.disabled)
      const previousSlotEntry = isPreviousDayStillValid
        ? slots[previousTempDate]?.find((slot) => slot.value === previousTempSlotId)
        : undefined
      const isPreviousSlotStillValid = Boolean(previousSlotEntry && !previousSlotEntry.disabled)

      if (isPreviousDayStillValid && isPreviousSlotStillValid) {
        // Re-pin both the date and slot explicitly even though nothing changed: previousTempDate/
        // previousTempSlotId may only be fallback-derived values (temp.date/slotId were never
        // actually set), and those fallbacks are recomputed from the now-reloaded delivery days on
        // the next render - they can each independently drift to a different landing day/slot
        // even though the pair we validated here is still fine.
        dispatch(tempActions.temp('date', previousTempDate))
        dispatch(tempActions.temp('slotId', previousTempSlotId))
        dispatch(tempActions.temp('addressChangeWarning', false))
      } else if (isPreviousDayStillValid) {
        // Same day is still deliverable, but the previously selected slot isn't - move to another
        // available slot on the same day rather than jumping to a different day. Pin the date
        // alongside it for the same fallback-drift reason as above.
        const nextValidSlotSameDay = slots[previousTempDate]?.find((slot) => !slot.disabled)

        dispatch(tempActions.temp('date', previousTempDate))
        dispatch(tempActions.temp('slotId', nextValidSlotSameDay?.value))
        dispatch(tempActions.temp('addressChangeWarning', 'slot'))
      } else {
        const nextValidDay = sortedDeliveryDays.find((deliveryDay) => !deliveryDay.disabled)

        if (nextValidDay) {
          // Only commit date/slotId when a genuinely valid day exists - dispatching temp('date',
          // undefined) would still set the key (Immutable's reducer does state.set(key, value)
          // unconditionally), and Map.get(key, fallback) only falls back when the key is absent,
          // not when it's present with an undefined value. That would permanently break the
          // landing-day fallback for every future read of these keys, not just this one.
          const nextValidSlot = slots[nextValidDay.date]?.find((slot) => !slot.disabled)

          dispatch(tempActions.temp('date', nextValidDay.date))
          dispatch(tempActions.temp('slotId', nextValidSlot?.value))
          dispatch(tempActions.temp('addressChangeWarning', 'day'))
        } else {
          // No delivery day at all is available for the new address - reset date/slotId/orderId
          // entirely (tempReset, not temp('date', undefined)) rather than leaving them pointing at
          // a day/slot that belonged to the old address. Leaving the stale pair in place would let
          // getDeliveryDaysAndSlots fall back to hasActiveSlotsForSelectedDate=true (its default
          // when there's no slots entry for that date), which could keep "Choose recipes" enabled
          // for a selection that no longer applies to this address at all.
          dispatch(tempActions.tempReset())
          // There's no replacement day/slot to point the warning at (tempDate ends up undefined),
          // so use the distinct 'unavailable' warning rather than 'day' - WarningAddressChanged
          // would otherwise format that undefined date via moment (which defaults to "now") and
          // tell the customer their delivery was moved to a date that was never actually chosen.
          dispatch(tempActions.temp('addressChangeWarning', 'unavailable'))
        }
      }

      closeThisModalIfStillCurrent(mySessionId)

      queueTrackingEvent<{
        action: 'confirm_delivery_address'
      }>({
        action: 'confirm_delivery_address',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      name={DELIVERY_ADDRESS_MODAL_NAME}
      onAfterClose={() => setAddressId(chosenAddress.get('id'))}
      onRequestClose={closeCurrentModal}
    >
      <ModalClose onClose={closeCurrentModal} />
      <ModalHeader>
        <Heading4>Edit delivery address</Heading4>
      </ModalHeader>
      <ModalBody>
        <StyledRadioContainer>
          <RadioGroup
            value={addressId}
            options={getRadioOptions()}
            onChange={(event) => setAddressId(event.target.value)}
            disabled={isSubmitting}
            outline
          />
        </StyledRadioContainer>
        <Space size={3} />
        <InfoBox />
      </ModalBody>
      <ModalFooter>
        <ModalButton
          aria-label="Confirm delivery address"
          width="100%"
          disabled={isSubmitting}
          onClick={() => changeAddress()}
        >
          Confirm delivery address
        </ModalButton>
      </ModalFooter>
    </Modal>
  )
}

export { DeliveryAddressModal }
